/**
 * Close-project suggestion predicate (Patch E18).
 *
 * Closes the lifecycle gap the spec leaves implicit: a project with all
 * parts billed (every live PART at stage 18) and the NDC already issued has
 * nothing left to do operationally — but the lifecycle still reads ACTIVE
 * until someone explicitly clicks Close Project. Before E18 the only nudge
 * to do so was a sibling tile in the dashboard; the banner is the in-page
 * reminder.
 *
 * The banner shows when ALL of:
 *   1. The project has at least one live PART
 *   2. Every live PART is at stage 18 (Cheque Received — billing complete)
 *   3. ndcIssuedDate IS set (the prior step in the chain is done — without
 *      this we'd double-banner with the NDC suggestion above)
 *   4. projectClosedAt IS null (it isn't already closed)
 *
 * The decision to gate on `ndcIssuedDate` rather than show both banners is
 * the right one — these are a sequential pair: "issue NDC" then "close
 * project". A user seeing both simultaneously would have to figure out
 * which to do first; the gating expresses the order explicitly.
 *
 * Pure, no React, no API access — easy to test.
 */
import type { Project } from './project-queries';
import type { ProjectItem } from './pipeline-queries';

export function shouldSuggestClose(
  project: Project,
  items: readonly ProjectItem[],
): boolean {
  if (project.ndcIssuedDate === null) return false; // NDC banner shows instead
  if (project.projectClosedAt !== null) return false; // already closed

  const liveParts = items.filter(
    (i) => i.itemType === 'PART' && i.deletedAt === null,
  );
  if (liveParts.length === 0) return false;
  return liveParts.every((p) => p.currentStageNumber === 18);
}
